import {frontlineOptionName} from '../../../../contracts/product-display-names-v1.ts';
import {displayMinorAmount} from '../../presentation/display-money-r31.ts';
import {useEffect,useRef,useState} from 'react';
import type {SyncedOptionSet} from '../../runtime/published-config-projection.ts';

const money=displayMinorAmount;

/** Presentation only. Selection, validation and prices remain with the caller. */
export function PublishedOptionTiles({sets,selected,onSelect,productCode}:{
  sets:readonly SyncedOptionSet[];
  selected:Readonly<Record<string,readonly string[]>>;
  onSelect:(set:SyncedOptionSet,optionId:string)=>void;
  productCode?:string;
}){
  const [open,setOpen]=useState<string|null>(null);
  const root=useRef<HTMLDivElement>(null);
  const buttons=useRef<Record<string,HTMLButtonElement|null>>({});
  const popup=useRef<HTMLDivElement>(null);
  useEffect(()=>{
    if(!open)return;
    const place=()=>{
      const node=popup.current,button=buttons.current[open];
      if(!node||!button)return;
      const viewport=window.visualViewport,left=viewport?.offsetLeft??0,top=viewport?.offsetTop??0;
      const width=viewport?.width??window.innerWidth,height=viewport?.height??window.innerHeight;
      const count=sets.find(set=>set.id===open)?.options.length??1;
      // ProductionViewport scales the Pad canvas. Fixed descendants use that
      // transformed canvas, while anchor rectangles use viewport coordinates.
      let host=node.parentElement;
      while(host&&getComputedStyle(host).transform==='none')host=host.parentElement;
      const hostBounds=host?.getBoundingClientRect();
      const scaleX=host&&hostBounds&&host.offsetWidth?hostBounds.width/host.offsetWidth:1;
      const scaleY=host&&hostBounds&&host.offsetHeight?hostBounds.height/host.offsetHeight:1;
      node.style.position='fixed';node.style.width=Math.min(768,Math.max(360,count*184+26),(width-48)/scaleX)+'px';
      node.style.maxWidth=(width-48)/scaleX+'px';node.style.maxHeight=(height-48)/scaleY+'px';node.style.overflowY='auto';
      const anchor=button.getBoundingClientRect(),bounds=node.getBoundingClientRect();
      const x=Math.max(left+24,Math.min(anchor.left,left+width-bounds.width-24));
      const y=Math.max(top+24,anchor.bottom+8+bounds.height<=top+height-24?anchor.bottom+8:Math.min(anchor.top-bounds.height-8,top+height-bounds.height-24));
      node.style.left=(x-(hostBounds?.left??0))/scaleX+'px';node.style.right='auto';
      node.style.top=(y-(hostBounds?.top??0))/scaleY+'px';
    };
    place();
    popup.current?.querySelector<HTMLButtonElement>(':scope > div button:not(:disabled)')?.focus({preventScroll:true});
    const outside=(event:PointerEvent)=>{if(!root.current?.contains(event.target as Node))setOpen(null);};
    document.addEventListener('pointerdown',outside);
    window.addEventListener('resize',place);document.addEventListener('scroll',place,true);
    window.visualViewport?.addEventListener('resize',place);
    return()=>{document.removeEventListener('pointerdown',outside);window.removeEventListener('resize',place);document.removeEventListener('scroll',place,true);window.visualViewport?.removeEventListener('resize',place);};
  },[open,sets]);
  const close=(id:string)=>{setOpen(null);buttons.current[id]?.focus();};
  return <div className="cfg19-portions" ref={root}>{sets.map(set=>{
    const chosen=set.options.filter(option=>(selected[set.id]??[]).includes(option.id));
    const single=set.selection==='SINGLE'&&set.options.length===1?set.options[0]:undefined;
    const disabled=!set.options.some(option=>option.active);
    const label=chosen.length?chosen.map(frontlineOptionName).join('、'):single?frontlineOptionName(single):set.name;
    return <div className="cfg19-portion" key={set.id}>
      <button type="button" ref={element=>{buttons.current[set.id]=element;}}
        className={'tone-'+(chosen.length?'pick':'base')+(open===set.id?' open':'')}
        aria-pressed={Boolean(chosen.length)} aria-label={single?frontlineOptionName(single):set.name+(chosen.length?'：'+label:'')}
        aria-expanded={single?undefined:open===set.id}
        disabled={disabled} onClick={()=>{
          if(single){onSelect(set,single.id);close(set.id);}
          else if(chosen.length&&set.selection==='SINGLE'){onSelect(set,chosen[0]!.id);close(set.id);}
          else setOpen(open===set.id?null:set.id);
        }}>
        <b>{label}</b>{chosen.length&&!single?<small>{set.name}</small>:null}{single?.priceAdjustmentMinor?<small>{single.priceAdjustmentMinor>0?'+':''}{money(single.priceAdjustmentMinor)}</small>:null}
      </button>
      {open===set.id?<div className="cfg19-pop" ref={popup} role="dialog" aria-label={set.name+' 份量'}
        onKeyDown={event=>{if(event.key==='Escape'){event.preventDefault();event.stopPropagation();close(set.id);}}}>
        <header><b>{set.name}</b><button type="button" aria-label={'關閉'+set.name} onClick={()=>close(set.id)}>×</button></header>
        <div>{set.options.map(option=>{
          const on=(selected[set.id]??[]).includes(option.id);
          const full=set.selection==='MULTI'&&!on&&(selected[set.id]??[]).length>=set.max;
          return <button type="button" key={option.id} disabled={!option.active||full} aria-pressed={on} className={on?'on':''}
            onClick={()=>{onSelect(set,option.id);if(set.selection==='SINGLE')close(set.id);}}>
            <b>{frontlineOptionName(option)}</b>{set.variant?.enabled&&productCode?<small>{(option.codePrefix??'')+productCode}</small>:null}
            {option.priceAdjustmentMinor?<small>{option.priceAdjustmentMinor>0?'+':''}{money(option.priceAdjustmentMinor)}</small>:null}
          </button>;
        })}</div>
      </div>:null}
    </div>;
  })}</div>;
}
